import React, { useRef, useState } from 'react';
import s from './TextOptionsBlock.module.css';
import { useSelector } from 'react-redux';
import { selectCurrentProject } from '../../../../store/selectors/projectSelectors';
import { useAppDispatch } from '../../../../store/store';
import { uploadDocument } from '../../../../shared/api/textProjectApi';
import { toast } from 'react-toastify';
import SplashLoader from './SplashLoader';
import IconCloudUpload from '../../../../shared/ui/icons/IconCloudUpload';

const ACCEPT_EXT = '.pdf,.doc,.docx';
const ACCEPT_MIME = new Set([
    'application/pdf',
    'application/msword',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
]);

const TextOptionsBlock: React.FC = () => {
    const currentProject = useSelector(selectCurrentProject);
    const dispatch = useAppDispatch();

    const [isUploading, setIsUploading] = useState(false);
    const [isDragOver, setIsDragOver] = useState(false);
    const inputRef = useRef<HTMLInputElement | null>(null);

    const disabled = isUploading;

    const handleOpenPicker = () => {
        if (disabled) return;
        inputRef.current?.click();
    };

    const validateAndUploadFile = async (file: File) => {
        const extOk = ACCEPT_EXT.split(',').some((ext) => file.name.toLowerCase().endsWith(ext));
        const mimeOk = file.type ? ACCEPT_MIME.has(file.type) : true;
        if (!extOk && !mimeOk) {
            toast.error('Unsupported file. Please select a PDF, DOCX, or DOC file.');
            return;
        }

        if (!currentProject?.id) {
            toast.error('No project selected.');
            return;
        }

        try {
            setIsUploading(true);
            await dispatch(uploadDocument({ file, projectId: currentProject.id }));
            toast.success('File uploaded successfully');
        } catch (error: any) {
            toast.error(error?.message || 'Failed to upload file');
        } finally {
            setIsUploading(false);
        }
    };

    const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = async (e) => {
        const file = e.target.files?.[0];
        e.currentTarget.value = '';

        if (!file) return;
        await validateAndUploadFile(file);
    };

    const handleDragEnter = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (!disabled) {
            setIsDragOver(true);
        }
    };

    const handleDragLeave = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);
    };

    const handleDragOver = (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
    };

    const handleDrop = async (e: React.DragEvent) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (disabled) return;

        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
            await validateAndUploadFile(files[0]);
        }
    };

    return (
        <>
            <div className={s.container}>
                <h3 className={s.title}>Text Options</h3>
                <p className={s.subtitle}>
                    Feel free to upload your text and tweak it as much as you'd like!
                </p>

                <input
                    ref={inputRef}
                    type="file"
                    accept={ACCEPT_EXT}
                    className={s.fileInput}
                    onChange={handleFileChange}
                />

                <div
                    className={`${s.uploadZone} ${isDragOver ? s.dragOver : ''} ${disabled ? s.disabled : ''}`}
                    onDragEnter={handleDragEnter}
                    onDragLeave={handleDragLeave}
                    onDragOver={handleDragOver}
                    onDrop={handleDrop}
                    onClick={handleOpenPicker}
                >
                    <div className={s.uploadContent}>
                        <div className={s.uploadIcon}>
                            <IconCloudUpload color={isDragOver ? '#FFDD35' : '#9CA3AF'} />
                        </div>

                        <div className={s.uploadText}>
                            <span className={s.clickText}>Click to upload</span>
                            <span className={s.dragText}> or drag and drop the document</span>
                        </div>
                        <p className={s.dragText}>Acceptable formats: .pdf, .docx, .doc</p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default TextOptionsBlock;
